import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
} from "react";
import { authApi } from "./api";

export type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

type ThemeContextType = {
	theme: Theme;
	resolvedTheme: ResolvedTheme;
	setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextType | null>(null);

const STORAGE_KEY = "decor-rocket-theme";
const VALID_THEMES: Theme[] = ["light", "dark", "system"];

function getSystemTheme(): ResolvedTheme {
	if (typeof window === "undefined") return "light";
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

function resolveTheme(theme: Theme): ResolvedTheme {
	return theme === "system" ? getSystemTheme() : theme;
}

function applyTheme(resolved: ResolvedTheme) {
	const root = document.documentElement;
	if (resolved === "dark") {
		root.classList.add("dark");
	} else {
		root.classList.remove("dark");
	}

	// Update theme-color meta tag
	const meta = document.querySelector('meta[name="theme-color"]');
	if (meta) {
		meta.setAttribute("content", resolved === "dark" ? "#1C1917" : "#E86F4A");
	}
}

function readStoredTheme(): Theme {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored && VALID_THEMES.includes(stored as Theme)) {
			return stored as Theme;
		}
	} catch {
		// localStorage unavailable
	}
	return "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [theme, setThemeState] = useState<Theme>(readStoredTheme);
	const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
		resolveTheme(readStoredTheme()),
	);

	// Apply theme to DOM whenever resolvedTheme changes
	useEffect(() => {
		applyTheme(resolvedTheme);
	}, [resolvedTheme]);

	// Listen for system theme changes when in "system" mode
	useEffect(() => {
		if (theme !== "system") return;

		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = (e: MediaQueryListEvent) => {
			const newResolved = e.matches ? "dark" : "light";
			setResolvedTheme(newResolved);
		};
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, [theme]);

	const setTheme = useCallback((newTheme: Theme) => {
		// Enable smooth transition
		document.documentElement.classList.add("theme-transitioning");

		setThemeState(newTheme);
		const resolved = resolveTheme(newTheme);
		setResolvedTheme(resolved);

		// Persist to localStorage (instant)
		try {
			localStorage.setItem(STORAGE_KEY, newTheme);
		} catch {
			// Ignore
		}

		// Sync to API (fire-and-forget)
		authApi
			.updatePreferences({ themePreference: newTheme })
			.catch(() => {});

		// Remove transition class after animation completes
		setTimeout(() => {
			document.documentElement.classList.remove("theme-transitioning");
		}, 300);
	}, []);

	return (
		<ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
			{children}
		</ThemeContext.Provider>
	);
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
}
